import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecoilState, useRecoilState } from 'recoil';
import {
  Box,
  FormControl,
  FormControlProps,
  FormLabel,
  HStack,
  LinkButton,
  SearchInput,
  SearchInputButton,
  SearchInputField,
  Typography,
} from '@tq/ui-components';

export interface SearchTextProps extends FormControlProps {
  searchState: RecoilState<string>;
  placeholderText: string;
  recordCount?: number;
  totalCount?: number;
  hideSearchInfo?: boolean;
  onChanged?: (value: string) => void;
}

export const SearchText: React.FC<SearchTextProps> = ({
  searchState,
  placeholderText,
  recordCount,
  totalCount,
  hideSearchInfo = false,
  onChanged,
  ...props
}) => {
  const { label, width } = props;
  const { t } = useTranslation('search_filter');
  // state is used if there is no recoil state used.
  const [text, setText] = useState<string>('');
  const [searchText, setSearchText] = useRecoilState(searchState);

  useEffect(() => setText(searchText), [setText, searchText]);

  const handleSearch = (value: string) => {
    setText(value);
    setSearchText(value);
    if (onChanged) {
      onChanged(value);
    }
  };

  const isInvalidSearch = () => {
    return recordCount === 0 && !!searchText;
  };

  const totalCountText = totalCount && totalCount > 0 ? `${t('of', 'of')} ${totalCount} ` : '';

  return (
    <Box w={width}>
      <FormControl>
        {label && <FormLabel htmlFor={'search-text'}>{label}</FormLabel>}
        <SearchInput
          inputid={'search-text'}
          aria-label={t('search_text_control', 'Search text control')}
          data-testid='text-search'
          isInvalid={isInvalidSearch()}
          title={t('search_text_title', 'Search')}
          value={text}
          onChange={(event) => setText((event as ChangeEvent<HTMLInputElement>).target.value)}
          onSearch={(value) => handleSearch(value ?? '')}
          searchOnCharacterCount={1000}
        >
          <SearchInputField
            aria-label={t('search_text_text', 'Search text')}
            placeholder={placeholderText ?? t('search_help_text', 'Type here to search')}
            maxLength={30}
          />
          <SearchInputButton
            data-testid='text-search-button'
            aria-label={t('search_button', 'Search button')}
          />
        </SearchInput>
        {searchText ? (
          <HStack mt={1} alignItems='start'>
            {!hideSearchInfo && (recordCount ?? 0) >= 0 ? (
              <Typography>
                <strong>
                  {`${t('showing', 'Showing')} ${recordCount} ${
                    recordCount === 1
                      ? t('search_result', 'search result')
                      : t('search_results', 'search results')
                  } ${totalCountText}${t('for', 'for')} '${searchText}'`}
                </strong>
              </Typography>
            ) : null}
            <LinkButton
              gridArea='link'
              mt={{ base: 3, lg: 0 }}
              onClick={() => handleSearch('')}
              whiteSpace='nowrap'
            >
              {t('clear_search', 'Clear search')}
            </LinkButton>
          </HStack>
        ) : null}
      </FormControl>
    </Box>
  );
};
